import React from 'react';

// The full-bleed treatment for every plugin-handoff / auth-notice screen
// (signing in, signed in, verify-email, not-whitelisted, handoff error): a cream
// ground, a centred column, and a bottom "ORGANIC ELECTRONICS" strip carrying
// one contextual link.
//
//   waveform  — the <HandoffWaveform/> canvas, when the screen has one
//   label     — the short uppercase status line
//   detail    — one line of copy under it
//   pulse     — breathe the label (work still in flight)
//   children  — extra actions rendered under the text block
//   action    — the bottom-strip link (Support, Sign out, …)
export function HandoffShell({
    waveform,
    label,
    detail,
    pulse = false,
    children,
    action,
}: {
    waveform?: React.ReactNode;
    label: string;
    detail: React.ReactNode;
    pulse?: boolean;
    children?: React.ReactNode;
    action?: React.ReactNode;
}) {
    return (
        <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-page-cream text-ink-secondary">
            <div className="flex flex-col items-center gap-[18px] px-5 py-24 text-center">
                {waveform}
                <div className="flex flex-col items-center gap-2.5">
                    <span
                        className={`text-[13px] font-semibold uppercase tracking-[0.18em] text-[rgba(42,40,36,0.6)] ${
                            pulse ? 'animate-textpulse' : ''
                        }`}
                    >
                        {label}
                    </span>
                    <span className="text-[14px] text-[rgba(42,40,36,0.5)]">{detail}</span>
                </div>
                {children && <div className="mt-6 flex flex-col items-center gap-3">{children}</div>}
            </div>

            <div className="absolute inset-x-0 bottom-0 px-[clamp(20px,4vw,40px)] pb-[26px]">
                <div className="mx-auto flex max-w-[560px] items-center justify-between gap-4 border-t border-[rgba(42,40,36,0.1)] pt-3.5">
                    <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.2em] text-[rgba(42,40,36,0.3)]">
                        Organic Electronics
                    </span>
                    {action}
                </div>
            </div>
        </div>
    );
}

// The recurring bottom-strip / inline link on these screens — a hairline-underlined
// charcoal control. Renders a <button> when `onClick` is given, else an <a>.
export function HandoffLink({
    children,
    href,
    onClick,
}: {
    children: React.ReactNode;
    href?: string;
    onClick?: () => void;
}) {
    const className =
        'border-b border-[rgba(42,40,36,0.25)] pb-0.5 text-[13px] text-[rgba(42,40,36,0.55)] transition-colors hover:text-ink-secondary';
    if (onClick) {
        return (
            <button type="button" onClick={onClick} className={className}>
                {children}
            </button>
        );
    }
    return (
        <a href={href} className={className}>
            {children}
        </a>
    );
}

export default HandoffShell;
